import TimerIcon from '@mui/icons-material/Timer';
import { InputAdornment, TextField } from '@mui/material';
import { useCallback, useEffect, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { clampCountdownSeconds, MAX_COUNTDOWN_SECONDS, MIN_COUNTDOWN_SECONDS } from '../../../utils/countdownTimerCodeUtils';

interface CountdownDurationHeaderFieldProps {
  seconds: number;
  disabled?: boolean;
  onCommit: (seconds: number) => void;
}

/** Compact duration editor for the widget header toolbar (commits on blur or Enter). */
const CountdownDurationHeaderField = ({ seconds, disabled, onCommit }: CountdownDurationHeaderFieldProps) => {
  const { t } = useTranslation();
  const [draftValue, setDraftValue] = useState(String(seconds));

  useEffect(() => {
    setDraftValue(String(seconds));
  }, [seconds]);

  const commitDraft = useCallback(() => {
    const parsed = Number(draftValue);
    const next = clampCountdownSeconds(Number.isFinite(parsed) ? parsed : seconds);
    setDraftValue(String(next));
    if (next !== seconds) {
      onCommit(next);
    }
  }, [draftValue, onCommit, seconds]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (disabled) return;
      setDraftValue(event.target.value);
    },
    [disabled]
  );

  const handleBlur = useCallback(() => {
    if (disabled) return;
    commitDraft();
  }, [commitDraft, disabled]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        commitDraft();
        (event.target as HTMLElement).blur();
      }
    },
    [commitDraft, disabled]
  );

  return (
    <TextField
      size="small"
      variant="outlined"
      value={draftValue}
      disabled={disabled}
      placeholder={t('CountdownTimerWidget.header.durationPlaceholder', 'Sec')}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      aria-label={t('CountdownTimerWidget.header.durationAriaLabel', 'Countdown duration in seconds')}
      sx={(theme) => ({
        width: 88,
        flexShrink: 0,
        '& .MuiOutlinedInput-root': {
          borderRadius: 20,
          height: 32,
          bgcolor: theme.vars?.palette?.action?.selected,
          '& fieldset': {
            borderColor: 'transparent',
          },
          '&:hover fieldset': {
            borderColor: theme.vars?.palette?.divider,
          },
          '&.Mui-focused fieldset': {
            borderColor: theme.vars?.palette?.primary?.main,
            borderWidth: 1,
          },
          '& input': {
            color: theme.vars?.palette?.text?.primary,
            textAlign: 'center',
            MozAppearance: 'textfield',
          },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '& .MuiInputAdornment-root': {
            color: theme.vars?.palette?.text?.primary,
          },
        },
      })}
      slotProps={{
        htmlInput: {
          type: 'number',
          inputMode: 'numeric',
          min: MIN_COUNTDOWN_SECONDS,
          max: MAX_COUNTDOWN_SECONDS,
        },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <TimerIcon fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

export default CountdownDurationHeaderField;
